import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import AxonallLogo from '../AxonallLogo';
import hotelService from '../../services/hotelService';

const HotelCardSkeleton = () => {
  return (
    <div className="bg-white border border-gray-200 overflow-hidden animate-pulse" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-2/5 relative min-h-[220px] bg-gray-100 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <AxonallLogo className="text-gray-300" size="text-4xl" color="text-gray-300" />
          </div>
        </div>
        <div className="w-full lg:w-3/5 p-6 lg:p-8">
          <div className="h-full flex flex-col justify-between">
            <div className="flex-1">
              <div className="w-3/4 h-6 bg-gray-100 mb-3"></div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-20 h-3 bg-gray-100"></div>
                <div className="w-16 h-3 bg-gray-100"></div>
              </div>
              <div className="flex items-start gap-2 mb-4">
                <div className="w-4 h-4 bg-gray-100"></div>
                <div className="flex-1">
                  <div className="w-2/3 h-3 bg-gray-100 mb-2"></div>
                  <div className="w-1/2 h-3 bg-gray-100"></div>
                </div>
              </div>
              <div className="mb-4">
                <div className="w-16 h-2 bg-gray-100 mb-2"></div>
                <div className="flex flex-wrap gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-16 h-5 bg-gray-100"></div>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3 mb-3">
                <div className="w-24 h-3 bg-gray-100 mb-2"></div>
                <div className="w-28 h-5 bg-gray-100"></div>
              </div>
            </div>
            <div className="flex items-end justify-between mt-6">
              <div>
                <div className="w-24 h-7 bg-gray-100 mb-1"></div>
                <div className="w-16 h-2 bg-gray-100"></div>
              </div>
              <div className="w-28 h-10 bg-gray-100"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HotelSearchResults = ({
  hasSearched,
  hotels,
  loading,
  locationQuery,
  searchForm,
  pagination,
  error,
  onBackToSearch,
  onPageChange,
  onCategoryFilterChange,
  formatDate,
  getRatingStars,
  formatPrice,
  safeString,
  searchType = 'location',
  hotelQuery = '',
  selectedHotelCode = '',
  onAreaSearch
}) => {
  const navigate = useNavigate();
  
  // Sorting state
  const [sortBy, setSortBy] = useState('default');
  
  // Category filter state
  const [categoryFilters, setCategoryFilters] = useState({
    all: true,
    recommended: false,
    compass: false
  });

  // Price fetching state
  const [priceFetching, setPriceFetching] = useState(false);
  const [hotelsWithPrices, setHotelsWithPrices] = useState([]);

  const [hotelsEmissions, setHotelsEmissions] = useState({});
  const [loadingEmissions, setLoadingEmissions] = useState({});

  const getCarbonTierColor = (tierName) => {
    if (!tierName || typeof tierName !== 'string') return 'bg-gray-200 text-gray-700';
    
    const tier = tierName.toLowerCase();
    if (tier.includes('low') || tier.includes('green')) return 'bg-green-100 text-green-800';
    if (tier.includes('medium') || tier.includes('moderate')) return 'bg-yellow-100 text-yellow-800';
    if (tier.includes('high') || tier.includes('impact')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatEmissions = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (value < 1) return value.toFixed(3);
    if (value < 10) return value.toFixed(2);
    if (value < 100) return value.toFixed(1);
    return Math.round(value);
  };

  const fetchHotelEmissions = async (hotel, hotelKey) => {
    const locationInfo = hotel.HotelInfo?.LocationInfo;
    const address = locationInfo?.Address;

    if (!address?.CountryName || !address?.CityName || hotelsEmissions[hotelKey]) {
      return;
    }

    setLoadingEmissions(prev => ({ ...prev, [hotelKey]: true }));

    try {
      let countryCode = '';
      if (address.CountryCode) {
        countryCode = typeof address.CountryCode === 'string' ? address.CountryCode : address.CountryCode.value || '';
      }

      let countryName = '';
      if (address.CountryName) {
        countryName = typeof address.CountryName === 'string' ? address.CountryName : address.CountryName.value || '';
      }

      let cityName = '';
      if (address.CityName) {
        cityName = typeof address.CityName === 'string' ? address.CityName : address.CityName.value || '';
      }

      const hotelLocation = {
        country: countryName,
        state: cityName,
        countryCode: countryCode,
      };

      const emissionsData = await hotelService.previewEmissions({
        hotelLocation,
        amenitiesList: [],
        ecoCertified: false,
        occupancy: 1,
        roomType: 'standard',
        selectedRoomType: null,
        numberOfNights: 1,
        numberOfRooms: 1,
      });

      setHotelsEmissions(prev => ({
        ...prev,
        [hotelKey]: emissionsData,
      }));

      console.log(`Fetched emissions for hotel ${hotelKey}:`, emissionsData);
    } catch (error) {
      console.error(`Error fetching emissions for hotel ${hotelKey}:`, error);
      setHotelsEmissions(prev => ({
        ...prev,
        [hotelKey]: null,
      }));
    } finally {
      setLoadingEmissions(prev => ({ ...prev, [hotelKey]: false }));
    }
  };

  useEffect(() => {
    if (hotels && hotels.length > 0) {
      hotels.forEach((hotel, index) => {
        const hotelKey = `search_${index}`;
        fetchHotelEmissions(hotel, hotelKey);
      });
    }
  }, [hotels]);

  // Effect to clear hotels when page changes or loading starts
  useEffect(() => {
    if (loading) {
      setHotelsWithPrices([]);
      setPriceFetching(false);
    }
  }, [loading, pagination?.currentPage]);

  // Effect to fetch missing prices when hotels are loaded
  useEffect(() => {
    if (hotels && hotels.length > 0 && !loading) {
      const hotelsWithoutPrices = hotels.filter(hotel => {
        const hasValidPricing = hotel.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate;
        return !hasValidPricing && hotel.HotelInfo?.HotelCode;
      });

      if (hotelsWithoutPrices.length > 0) {
        fetchMissingPrices(hotelsWithoutPrices);
      } else {
        // All hotels already have pricing, set them directly
        setHotelsWithPrices(hotels);
      }
    } else if (hotels && hotels.length > 0) {
      setHotelsWithPrices(hotels);
    }
  }, [hotels, loading]);

  // Function to fetch missing prices
  const fetchMissingPrices = async (hotelsToUpdate) => {
    if (priceFetching) return; // Prevent multiple simultaneous requests
    
    setPriceFetching(true);
    try {
      const response = await hotelService.fetchMissingPrices(hotelsToUpdate, searchForm);
      
      if (response.success && response.hotels) {
        // Merge the updated hotels with the original hotels
        const updatedHotels = hotels.map(hotel => {
          const updatedHotel = response.hotels.find(h => 
            h.HotelInfo?.HotelCode === hotel.HotelInfo?.HotelCode
          );
          return updatedHotel || hotel;
        });
        
        setHotelsWithPrices(updatedHotels);

      }
    } catch (error) {
      console.error('Failed to fetch missing prices:', error);
      // If price fetching fails, still show the original hotels
      setHotelsWithPrices(hotels);
    } finally {
      setPriceFetching(false);
    }
  };

  // Handle category filter changes
  const handleCategoryFilterChange = (filterType) => {
    const nextFilters = filterType === 'all'
      ? { all: true, recommended: false, compass: false }
      : {
          ...categoryFilters,
          all: false,
          [filterType]: !categoryFilters[filterType]
        };

    if (!nextFilters.recommended && !nextFilters.compass) {
      nextFilters.all = true;
    }

    setCategoryFilters(nextFilters);
    if (searchType !== 'hotel' && onCategoryFilterChange) {
      onCategoryFilterChange(nextFilters);
    }
  };

  // Sort hotels based on selected criteria
  const sortedHotels = useMemo(() => {
    const hotelsToUse = hotelsWithPrices.length > 0 ? hotelsWithPrices : hotels;
    if (!hotelsToUse || hotelsToUse.length === 0) return hotelsToUse;
    
    const filteredHotels = searchType === 'hotel'
      ? hotelsToUse.filter(hotel => {
          const hasValidPricing = hotel.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate;
          const hasImage = hotel.HotelImageInfo?.ImageItem?.Image?.Url ||
                         hotel.HotelMedia?.ImageItem?.Image?.Url ||
                         hotel.images?.ImageItem?.Image?.Url ||
                         hotel.media?.ImageItem?.Image?.Url;
          const hotelCategory = hotel.categoryInfo?.displayCategory;
          let passesCategoryFilter = true;

          if (!categoryFilters.all) {
            if (categoryFilters.recommended && hotelCategory === 'Recommended') {
              passesCategoryFilter = true;
            } else if (categoryFilters.compass && hotelCategory === 'Compass') {
              passesCategoryFilter = true;
            } else if (!categoryFilters.recommended && !categoryFilters.compass) {
              passesCategoryFilter = !hotelCategory || hotelCategory === 'No Chain Code';
            } else {
              passesCategoryFilter = false;
            }
          }

          return hasValidPricing && hasImage && passesCategoryFilter;
        })
      : hotelsToUse;

    if (sortBy === 'default') return filteredHotels;

    return [...filteredHotels].sort((a, b) => {
      switch (sortBy) {
        case 'price-low-high':
          const priceA = a.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate || 0;
          const priceB = b.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate || 0;
          return priceA - priceB;
        
        case 'price-high-low':
          const priceAHigh = a.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate || 0;
          const priceBHigh = b.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]?.AverageNightlyRate || 0;
          return priceBHigh - priceAHigh;
        
        case 'rating-high-low':
          const ratingA = parseFloat(a.HotelInfo?.SabreRating) || 0;
          const ratingB = parseFloat(b.HotelInfo?.SabreRating) || 0;
          return ratingB - ratingA;
        
        case 'rating-low-high':
          const ratingALow = parseFloat(a.HotelInfo?.SabreRating) || 0;
          const ratingBLow = parseFloat(b.HotelInfo?.SabreRating) || 0;
          return ratingALow - ratingBLow;
        
        default:
          return 0;
      }
    });
  }, [hotels, hotelsWithPrices, sortBy, categoryFilters, searchType]);

  // Handle hotel details navigation
  const handleViewHotelDetails = (hotel) => {
    const hotelCode = hotel.HotelInfo?.HotelCode || hotel.HotelInfo?.SabreHotelCode;
    if (!hotelCode) {
      console.error('Hotel code not found for hotel:', hotel);
      return;
    }

    // Create search parameters for hotel details page
    const searchParams = new URLSearchParams({
      checkInDate: searchForm.checkInDate,
      checkOutDate: searchForm.checkOutDate,
      adults: searchForm.adults.toString(),
      children: searchForm.children.toString(),
      currency: searchForm.currency || 'USD'
    });

    // Add child ages if present
    if (searchForm.children > 0 && searchForm.childAges) {
      searchParams.set('childAges', searchForm.childAges);
    }

    // Open hotel details page in new tab
    window.open(`/hotel/${hotelCode}?${searchParams.toString()}`, '_blank');
  };
  if (!hasSearched) {
    return null;
  }

  return (
    <section className="relative z-10 font-poppinsLight max-w-full min-w-0">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full min-w-0">
        {/* Results Header */}
        <div className="mb-4 relative">
          <div className="bg-white border border-gray-200 p-4 sm:p-5" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] tracking-[0.32em] uppercase font-poppinsLight text-gray-400 mb-1">
                  {searchType === 'hotel' ? 'Availability' : 'Search Results'}
                </p>
                <h1 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 break-words"
                  style={{ fontSize: 'clamp(1.1rem, 2vw, 1.6rem)' }}>
                  {searchType === 'hotel' && hotelQuery ? hotelQuery :
                   locationQuery === 'this area' ? 'Hotels in This Area' :
                   locationQuery || 'Selected Location'}
                </h1>
                {searchType === 'hotel' && (
                  <p className="text-[10px] font-poppinsLight text-gray-500 mt-1 uppercase tracking-wider">
                    {hotels.length > 0 ? 'Available for your stay' : 'Not available for selected dates'}
                  </p>
                )}
              </div>
              <div className="md:ml-4 flex flex-col items-stretch md:items-end text-left md:text-right w-full md:w-auto shrink-0 border-t border-gray-100 pt-3 md:border-t-0 md:pt-0">
                <p className="text-xs font-poppinsLight text-gray-500 mb-0.5">
                  {formatDate(searchForm.checkInDate)} — {formatDate(searchForm.checkOutDate)}
                </p>
                <p className="text-xs font-poppinsLight text-gray-500">
                  {searchForm.adults} {searchForm.adults === 1 ? 'Guest' : 'Guests'}
                  {searchForm.children > 0 && `, ${searchForm.children} ${searchForm.children === 1 ? 'Child' : 'Children'}`}
                </p>
              </div>
            </div>

            {priceFetching && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-100">
                <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin shrink-0"></div>
                <span className="text-[10px] font-poppinsLight text-gray-600 tracking-wide">Fetching prices…</span>
              </div>
            )}

            {!loading && hotels.length > 0 && (
              <div className="mt-4 flex flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'all', label: 'All Hotels' },
                    { key: 'recommended', label: 'Recommended' },
                    { key: 'compass', label: 'Compass' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleCategoryFilterChange(key)}
                      className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular transition-colors duration-200 ${
                        categoryFilters[key]
                          ? 'bg-gray-900 text-white'
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 shrink-0">Sort</p>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 bg-white border border-gray-200 text-xs font-poppinsLight text-gray-700 outline-none focus:border-gray-900 transition-colors duration-200"
                  >
                    <option value="default">Default</option>
                    <option value="price-low-high">Price: Low → High</option>
                    <option value="price-high-low">Price: High → Low</option>
                    <option value="rating-high-low">Rating: High → Low</option>
                    <option value="rating-low-high">Rating: Low → High</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="space-y-6">
            {[...Array(3)].map((_, index) => (
              <HotelCardSkeleton key={index} />
            ))}
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <AxonallLogo className="text-gray-400 animate-pulse" size="text-2xl" color="text-gray-400" />
              <p className="text-[10px] tracking-[0.25em] uppercase font-poppinsLight text-gray-400">Finding hotels…</p>
            </div>
          </div>
        )}

        {!loading && hotels.length === 0 ? (
          <div className="flex flex-col items-center py-16 px-4 text-center">
            <svg className="w-10 h-10 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="text-[9px] tracking-[0.35em] uppercase font-poppinsLight text-gray-400 mb-2">No Results</p>
            <h3 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 text-xl mb-3">
              {searchType === 'hotel' ? 'Hotel Not Available' : 'No Hotels Found'}
            </h3>
            <p className="font-poppinsLight text-gray-500 text-sm max-w-sm mb-6">
              {searchType === 'hotel'
                ? `${hotelQuery} is not available for the selected dates. Try different dates or search nearby.`
                : 'Try adjusting your search criteria or expanding your search radius.'}
            </p>
            {searchType === 'hotel' && (
              <button
                onClick={async () => { if (onAreaSearch) await onAreaSearch(); }}
                disabled={loading}
                className="px-6 py-3 bg-gray-900 text-white text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? <><LoadingSpinner /><span>Searching…</span></> : 'Search Hotels Nearby'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {sortedHotels.map((hotel, index) => {
              const cityName = hotel.HotelInfo?.LocationInfo?.Address?.CityName?.value || 
                              hotel.HotelInfo?.LocationInfo?.Address?.CityName || '';
              const countryName = hotel.HotelInfo?.LocationInfo?.Address?.CountryName?.value || 
                                 hotel.HotelInfo?.LocationInfo?.Address?.CountryName || '';
              
              return (
              <div key={index} className="group bg-white border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors duration-200" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="flex flex-col lg:flex-row">
                  {/* Hotel Image Section */}
                  <div className="w-full lg:w-2/5 relative min-h-[200px] sm:min-h-[240px] lg:min-h-[260px] bg-gray-100 overflow-hidden">
                    {(() => {
                      const imageUrl = hotel.HotelImageInfo?.ImageItem?.Image?.Url || 
                                     hotel.HotelMedia?.ImageItem?.Image?.Url ||
                                     hotel.images?.ImageItem?.Image?.Url ||
                                     hotel.media?.ImageItem?.Image?.Url;
                      return imageUrl ? (
                        <img 
                          src={imageUrl}
                          alt={hotel.HotelInfo?.HotelName || 'Hotel'}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null;
                    })()}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400" style={{display: (() => {
                      const imageUrl = hotel.HotelImageInfo?.ImageItem?.Image?.Url || 
                                     hotel.HotelMedia?.ImageItem?.Image?.Url ||
                                     hotel.images?.ImageItem?.Image?.Url ||
                                     hotel.media?.ImageItem?.Image?.Url;
                      return imageUrl ? 'none' : 'flex';
                    })()}}>
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    
                    {hotel.HotelInfo?.PropertyQualityInfo?.PropertyQuality?.[0]?.Description && (
                      <div className="absolute top-3 left-3 bg-gray-900 text-white px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-poppinsRegular">
                        {hotel.HotelInfo.PropertyQualityInfo.PropertyQuality[0].Description}
                      </div>
                    )}
                    {hotel.categoryInfo?.displayCategory && 
                     hotel.categoryInfo.displayCategory !== 'No Chain Code' && (
                      <div className="absolute top-3 right-3 px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-poppinsRegular bg-gray-900 text-white">
                        {hotel.categoryInfo.displayCategory}
                      </div>
                    )}
                  </div>
                    
                  {/* Hotel Details Section */}
                  <div className="w-full lg:w-3/5 p-4 sm:p-6 lg:p-7 relative min-w-0">
                    <div className="h-full flex flex-col justify-between">
                      <div className="flex-1">
                        <h2 className="font-AXONALLPrimeRegular uppercase tracking-wide text-gray-900 mb-2 leading-tight break-words"
                          style={{ fontSize: 'clamp(1rem, 1.8vw, 1.4rem)' }}>
                          {hotel.HotelInfo?.HotelName || 'Hotel Name Not Available'}
                        </h2>
                        
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {hotel.HotelInfo?.BrandName && 
                           hotel.HotelInfo.BrandName !== 'UNDEFINED' && 
                           hotel.HotelInfo.BrandName.trim() !== '' && (
                            <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-500 border border-gray-200 px-2 py-0.5">
                              {hotel.HotelInfo.BrandName}
                            </span>
                          )}
                          {hotel.HotelInfo?.Category?.Description && 
                           hotel.HotelInfo.Category.Description !== 'UNDEFINED' && 
                           hotel.HotelInfo.Category.Description.trim() !== '' && (
                            <span className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-500">
                              {hotel.HotelInfo.Category.Description}
                            </span>
                          )}
                          {hotel.HotelInfo?.SabreRating && (
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <svg key={i} className={`w-3 h-3 ${i < parseFloat(hotel.HotelInfo.SabreRating) ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {hotel.HotelInfo?.LocationInfo?.Address && (
                          <div className="flex items-start gap-1.5 mb-4">
                            <svg className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div>
                              <p className="text-xs font-poppinsLight text-gray-600">
                                {hotel.HotelInfo.LocationInfo.Address.AddressLine1}
                                {hotel.HotelInfo.LocationInfo.Address.AddressLine2 && `, ${hotel.HotelInfo.LocationInfo.Address.AddressLine2}`}
                              </p>
                              <p className="text-xs font-poppinsLight text-gray-400">
                                {cityName && `${cityName}, `}{countryName}
                              </p>
                            </div>
                          </div>
                        )}

                        {hotel.HotelInfo?.Amenities?.Amenity && (
                          <div className="mb-4">
                            <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 mb-1.5">Amenities</p>
                            <div className="flex flex-wrap gap-1.5">
                              {hotel.HotelInfo.Amenities.Amenity
                                .filter(a => a.Description && a.Description !== 'UNDEFINED' && a.Description.trim() !== '')
                                .slice(0, 6)
                                .map((amenity, amenityIndex) => (
                                <span key={amenityIndex} className="px-2 py-1 bg-gray-50 border border-gray-100 text-[9px] tracking-[0.1em] uppercase font-poppinsLight text-gray-500">
                                  {amenity.Description}
                                </span>
                              ))}
                              {hotel.HotelInfo.Amenities.Amenity.length > 6 && (
                                <span className="px-2 py-1 bg-gray-50 border border-gray-100 text-[9px] font-poppinsLight text-gray-400">
                                  +{hotel.HotelInfo.Amenities.Amenity.length - 6} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {(() => {
                          const hotelKey = `search_${hotels.indexOf(hotel)}`;
                          const emissionsData = hotelsEmissions[hotelKey];
                          const isLoading = loadingEmissions[hotelKey];
                          if (isLoading) {
                            return (
                              <div className="border-t border-gray-100 pt-3 mb-3">
                                <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 mb-1">Environmental Impact</p>
                                <div className="w-28 h-4 bg-gray-100 animate-pulse"></div>
                              </div>
                            );
                          }
                          if (emissionsData?.data) {
                            const emissions = emissionsData.data;
                            const tierNameRaw = emissions.carbonTier?.tierName || emissions.tierName || emissions.carbonTier;
                            const tierName = (typeof tierNameRaw === 'string') ? tierNameRaw : null;
                            const emissionValue = emissions.carbonEmission || null;
                            if (!tierName && !emissionValue) return null;
                            return (
                              <div className="border-t border-gray-100 pt-3 mb-3">
                                <p className="text-[9px] tracking-[0.2em] uppercase font-poppinsLight text-gray-400 mb-2">Environmental Impact</p>
                                <div className="flex items-center gap-3">
                                  {emissionValue !== null && (
                                    <p className="text-sm font-poppinsRegular text-gray-900">
                                      {formatEmissions(emissionValue)}{' '}
                                      <span className="text-[10px] font-poppinsLight text-gray-500">kg CO₂/night</span>
                                    </p>
                                  )}
                                  {tierName && (
                                    <span className="text-[9px] tracking-[0.15em] uppercase font-poppinsLight text-gray-500 border border-gray-200 px-1.5 py-0.5">
                                      {tierName}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      {/* Bottom Section with Price and Button */}
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mt-5 pt-4 border-t border-gray-100">
                        <div className="min-w-0">
                          <p className="font-AXONALLPrimeRegular text-gray-900 text-2xl tracking-wide">
                            {formatPrice(hotel.HotelRateInfo.RateInfos.ConvertedRateInfo[0].AverageNightlyRate, 
                                        hotel.HotelRateInfo.RateInfos.ConvertedRateInfo[0].CurrencyCode || searchForm.currency)}
                          </p>
                          <p className="text-[9px] uppercase tracking-[0.2em] font-poppinsLight text-gray-400 mt-0.5">per night · best available rate</p>
                        </div>
                        <button 
                          onClick={() => handleViewHotelDetails(hotel)}
                          className="bg-gray-900 text-white px-6 py-3 text-[10px] tracking-[0.25em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity duration-200 flex items-center justify-center gap-2 w-full sm:w-auto shrink-0"
                        >
                          View Hotel
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              );
            })}

            {/* Pagination */}
            {pagination.totalItems > pagination.itemsPerPage && (
              <div className="flex flex-col sm:flex-row flex-wrap justify-center items-stretch sm:items-center gap-3 py-8 px-1">
                <button
                  onClick={() => onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 w-full sm:w-auto"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                
                <div className="flex items-center justify-center gap-3 px-5 py-2.5 bg-white border border-gray-200 w-full sm:w-auto">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-poppinsRegular text-gray-700">
                    Page {pagination.currentPage}
                  </span>
                  <div className="w-px h-3 bg-gray-200"></div>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-poppinsLight text-gray-400">
                    of {Math.ceil(pagination.totalItems / pagination.itemsPerPage)}
                  </span>
                </div>
                
                <button
                  onClick={() => onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= Math.ceil(pagination.totalItems / pagination.itemsPerPage)}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 w-full sm:w-auto"
                >
                  Next
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default HotelSearchResults;
